import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileText,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDemoData } from '../useDemoData'
import type { Paper } from '../../api/types'

type SetupTab = 'rubric' | 'questions' | 'papers'

export default function DemoExamSetup() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<SetupTab>('rubric')
  const { t } = useTranslation('pages')

  const examId = Number(sessionId)
  const { examDetails, studentsByClass } = useDemoData()
  const detail = examDetails[examId] ?? null

  // Set active tab from query params on mount
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'questions' || tab === 'papers') {
      setActiveTab(tab)
    }
  }, [searchParams])

  if (!detail) {
    return (
      <div className="animate-fade-in">
        <div className="flex items-center gap-16 mb-24">
          <button className="btn btn-outline" onClick={() => navigate('/demo/exams')}>
            <ArrowLeft size={15} />
            {t('common.back')}
          </button>
        </div>
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-bg)',
            color: 'var(--color-danger)',
          }}
        >
          {t('demo.examSetup.examNotFound')}
        </div>
      </div>
    )
  }

  const rubricFileId = detail.rubric_file_id || null
  const hasRubric = Boolean(
    rubricFileId && rubricFileId !== 'null' && rubricFileId !== 'undefined',
  )

  const classStudents = detail.class_id ? studentsByClass[detail.class_id] || [] : []

  const tabs: { key: SetupTab; label: string; count?: string }[] = [
    {
      key: 'rubric',
      label: t('demo.examSetup.tabRubric'),
      count: hasRubric ? '1' : undefined,
    },
    {
      key: 'questions',
      label: t('demo.examSetup.tabQuestions'),
      count: detail.questions.length ? String(detail.questions.length) : undefined,
    },
    {
      key: 'papers',
      label: t('demo.examSetup.tabPapers'),
      count: `${detail.papers.length}/${classStudents.length || detail.papers.length || 0}`,
    },
  ]

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-16 mb-24">
        <button
          className="btn btn-outline"
          onClick={() => navigate(`/demo/exams/${sessionId}`)}
        >
          <ArrowLeft size={15} />
          {t('demo.examSetup.backToExam')}
        </button>
        <h2
          className="section-title"
          style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}
        >
          {t('demo.examSetup.pageTitle')}
          <span className="text-sm text-muted" style={{ fontWeight: 400 }}>
            {detail.session_name}
          </span>
        </h2>
      </div>

      {/* Status summary row */}
      <div className="setup-status-row">
        <span className={`chip ${hasRubric ? 'chip-check' : 'chip-missing'}`}>
          {hasRubric ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          {t('demo.examSetup.rubricChip')}
        </span>
        <span
          className={`chip ${detail.questions.length > 0 ? 'chip-check' : 'chip-missing'}`}
        >
          {detail.questions.length > 0 ? (
            <CheckCircle2 size={14} />
          ) : (
            <AlertCircle size={14} />
          )}
          {t('demo.examSetup.questionsChip')} {detail.questions.length}
        </span>
        <span className="chip chip-papers">
          {detail.papers.length} / {classStudents.length || detail.papers.length || 0} {t('demo.examSetup.paperSuffix', { count: detail.papers.length })}
        </span>
      </div>

      {/* Tab navigation */}
      <div className="tabs mb-0">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && <span className="tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      <div className="setup-panel animate-fade-in">
        {/* RUBRIC TAB */}
        {activeTab === 'rubric' && (
          <div className="animate-fade-in">
            <p className="text-sm text-muted mb-20">
              {t('demo.examSetup.rubricTabDesc')}
            </p>

            {hasRubric && (
              <div className="upload-file-row">
                <div className="upload-file-icon">
                  <FileText size={16} />
                </div>
                <span className="chip chip-check">
                  <CheckCircle2 size={13} />
                  {t('demo.examSetup.rubricLoaded')}
                </span>
              </div>
            )}

            {/* Rubric preview */}
            {detail.rubric_preview && (
              <div
                className="card card-padding mb-20"
                style={{ fontSize: 'var(--text-sm)' }}
              >
                {detail.rubric_preview.summary && (
                  <p style={{ marginBottom: 8 }}>{detail.rubric_preview.summary}</p>
                )}
                {detail.rubric_preview.visual_metadata_text && (
                  <p className="text-muted" style={{ marginBottom: 8, fontStyle: "italic" }}>
                    {detail.rubric_preview.visual_metadata_text}
                  </p>
                )}
                {detail.rubric_preview.criteria?.length > 0 && (
                  <ul style={{ paddingLeft: 16, margin: 0 }}>
                    {detail.rubric_preview.criteria.map((c, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        <strong>{c.name}</strong>: {c.expectation}
                        {c.weight_or_points && (
                          <span className="text-muted"> ({c.weight_or_points})</span>
                        )}
                        {c.reference_answer && (
                          <div className="text-muted" style={{ marginTop: 2 }}>
                            {t('common.referenceAnswer')} {c.reference_answer}
                          </div>
                        )}
                        {c.visual_metadata_text && (
                          <div className="text-muted" style={{ marginTop: 2, fontStyle: "italic" }}>
                            {c.visual_metadata_text}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Disabled upload zone */}
            <div
              className="upload-zone"
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              <Upload size={28} className="upload-zone-icon" />
              <p className="upload-zone-label">{t('demo.examSetup.uploadDisabled')}</p>
              <p className="upload-zone-hint">{t('demo.examSetup.rubricFileHint')}</p>
            </div>
          </div>
        )}

        {/* QUESTIONS TAB */}
        {activeTab === 'questions' && (
          <div className="animate-fade-in">
            {detail.questions.length > 0 ? (
              <div className="upload-file-row">
                <div className="upload-file-icon">
                  <FileText size={16} />
                </div>
                <span className="chip chip-check">
                  <CheckCircle2 size={13} />
                  {t('demo.examSetup.questionsLoaded')}
                </span>
              </div>
            ) : (
              <div
                className="upload-zone"
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                <Upload size={28} className="upload-zone-icon" />
                <p className="upload-zone-label">{t('demo.examSetup.uploadDisabled')}</p>
              </div>
            )}

            {/* Existing questions list */}
            {detail.questions.length > 0 && (
              <>
                <div className="flex flex-col gap-12 mt-12">
                  {detail.questions.map((q) => (
                    <div key={q.id} className="question-row">
                      <span className="question-row-num">S{q.question_number}</span>
                      <div className="question-row-body">
                        <div
                          className="form-input"
                          style={{ background: 'var(--bg-primary)', cursor: 'default' }}
                        >
                          {q.question_text || t('demo.examSetup.questionTextPlaceholder')}
                        </div>
                        <div className="question-row-pts">
                          <span className="text-xs text-muted">{t('demo.examSetup.maxPointsLabel')}</span>
                          <div
                            className="form-input"
                            style={{
                              width: 72,
                              background: 'var(--bg-primary)',
                              cursor: 'default',
                            }}
                          >
                            {q.max_points}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* PAPERS TAB */}
        {activeTab === 'papers' && (
          <div className="animate-fade-in">
            <p className="text-sm text-muted mb-20">
              {t('demo.examSetup.papersTabDesc')}
            </p>

            {/* Student selection — read-only in demo */}
            {detail.class_id ? (
              <div className="form-group mb-20">
                <label className="form-label">{t('demo.examSetup.assignPapersLabel')}</label>
                <select className="form-select" disabled>
                  <option>{t('demo.examSetup.assignDisabled')}</option>
                </select>
              </div>
            ) : (
              <div className="card card-padding mb-20">
                <p className="text-sm text-muted">
                  {t('demo.examSetup.noClassLinked')}
                </p>
              </div>
            )}

            {/* Disabled upload zone */}
            <div
              className="upload-zone"
              style={{ marginBottom: 20, opacity: 0.5, cursor: 'not-allowed' }}
            >
              <Upload size={28} className="upload-zone-icon" />
              <p className="upload-zone-label">{t('demo.examSetup.uploadDisabled')}</p>
              <p className="upload-zone-hint">{t('demo.examSetup.paperFileHint')}</p>
            </div>

            {/* Papers list */}
            {detail.papers.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-12 mt-20">{t('demo.examSetup.uploadedPapers')}</h4>
                <div className="papers-list">
                  {detail.papers.map((p: Paper) => (
                    <div key={p.id} className="upload-file-row">
                      <div className="upload-file-icon upload-file-icon-sm">
                        <FileText size={16} />
                      </div>
                      <div className="upload-file-info">
                        <div className="upload-file-name-compact">
                          <span className="upload-file-student">{p.student_name}</span>
                          <span className="upload-file-filename">{p.original_filename}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detail.papers.length === 0 && (
              <div className="text-sm text-muted mt-16" style={{ textAlign: 'center' }}>
                {t('demo.examSetup.noPapersUploaded')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
